// Minimap component for viewport navigation

import type { Room, Corridor, HexLayout, Point } from "../types.ts";
import type { StateDiff } from "../undo/manager.ts";
import { hexToPixel, keyToHex } from "../hex/coordinates.ts";

export interface MinimapConfig {
  width: number;
  height: number;
  padding: number;
}

const DEFAULT_CONFIG: MinimapConfig = {
  width: 180,
  height: 140,
  padding: 10,
};

/**
 * Minimap showing all rooms with viewport indicator
 */
export class Minimap {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: MinimapConfig;

  // World bounds (in hex pixel coordinates)
  private worldBounds: { minX: number; maxX: number; minY: number; maxY: number } | null = null;

  // Callback when user clicks to navigate
  private onNavigate: ((worldX: number, worldY: number) => void) | null = null;

  // Diff state for undo tree hover preview
  private diffState: StateDiff | null = null;
  private diffLayout: HexLayout | null = null;

  constructor(container: HTMLElement, config: Partial<MinimapConfig> = {}) {
    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Create canvas element
    this.canvas = document.createElement("canvas");
    this.canvas.className = "minimap-canvas"; // Exclude from generic canvas styles
    this.canvas.width = this.config.width;
    this.canvas.height = this.config.height;

    // Style the canvas - position in upper right
    // Using cssText to ensure our styles take full precedence
    this.canvas.style.cssText = `
      position: absolute !important;
      top: 70px !important;
      right: 10px !important;
      left: auto !important;
      border: 2px solid #555;
      border-radius: 4px;
      background-color: rgba(30, 30, 30, 0.9);
      cursor: pointer;
      z-index: 100;
      width: ${this.config.width}px;
      height: ${this.config.height}px;
    `;

    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d context for minimap");
    this.ctx = ctx;

    // Add click handler
    this.canvas.addEventListener("click", this.handleClick);
    this.canvas.addEventListener("mousedown", this.handleMouseDown);

    this.container.appendChild(this.canvas);
  }

  /**
   * Set the navigation callback
   */
  setOnNavigate(callback: (worldX: number, worldY: number) => void): void {
    this.onNavigate = callback;
  }

  /**
   * Set the diff state for undo tree hover preview.
   * The minimap will render added rooms in green and removed rooms in red.
   */
  setDiffState(diff: StateDiff, layout: HexLayout): void {
    this.diffState = diff;
    this.diffLayout = layout;
  }

  /**
   * Clear the diff state, returning to normal view.
   */
  clearDiff(): void {
    this.diffState = null;
    this.diffLayout = null;
  }

  /**
   * Handle click on minimap to navigate
   */
  private handleClick = (e: MouseEvent): void => {
    if (!this.worldBounds || !this.onNavigate) return;

    const rect = this.canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert minimap coords to world coords
    const worldCoords = this.minimapToWorld(clickX, clickY);
    if (worldCoords) {
      this.onNavigate(worldCoords.x, worldCoords.y);
    }
  };

  /**
   * Handle mouse down for drag navigation
   */
  private handleMouseDown = (e: MouseEvent): void => {
    if (!this.worldBounds || !this.onNavigate) return;

    const handleDrag = (moveEvent: MouseEvent) => {
      const rect = this.canvas.getBoundingClientRect();
      const clickX = moveEvent.clientX - rect.left;
      const clickY = moveEvent.clientY - rect.top;

      const worldCoords = this.minimapToWorld(clickX, clickY);
      if (worldCoords && this.onNavigate) {
        this.onNavigate(worldCoords.x, worldCoords.y);
      }
    };

    const handleUp = () => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", handleUp);
    };

    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleUp);
  };

  /**
   * Convert minimap coordinates to world coordinates
   */
  private minimapToWorld(mx: number, my: number): Point | null {
    if (!this.worldBounds) return null;

    const { padding, width, height } = this.config;
    const drawWidth = width - padding * 2;
    const drawHeight = height - padding * 2;

    const worldWidth = this.worldBounds.maxX - this.worldBounds.minX;
    const worldHeight = this.worldBounds.maxY - this.worldBounds.minY;

    // Handle empty world
    if (worldWidth <= 0 || worldHeight <= 0) return null;

    const scale = Math.min(drawWidth / worldWidth, drawHeight / worldHeight);

    // Calculate offset to center the content
    const scaledWidth = worldWidth * scale;
    const scaledHeight = worldHeight * scale;
    const offsetX = padding + (drawWidth - scaledWidth) / 2;
    const offsetY = padding + (drawHeight - scaledHeight) / 2;

    // Reverse the transform
    const worldX = (mx - offsetX) / scale + this.worldBounds.minX;
    const worldY = (my - offsetY) / scale + this.worldBounds.minY;

    return { x: worldX, y: worldY };
  }

  /**
   * Convert world coordinates to minimap coordinates
   */
  private worldToMinimap(wx: number, wy: number): Point | null {
    if (!this.worldBounds) return null;

    const { padding, width, height } = this.config;
    const drawWidth = width - padding * 2;
    const drawHeight = height - padding * 2;

    const worldWidth = this.worldBounds.maxX - this.worldBounds.minX;
    const worldHeight = this.worldBounds.maxY - this.worldBounds.minY;

    if (worldWidth <= 0 || worldHeight <= 0) return null;

    const scale = Math.min(drawWidth / worldWidth, drawHeight / worldHeight);

    const scaledWidth = worldWidth * scale;
    const scaledHeight = worldHeight * scale;
    const offsetX = padding + (drawWidth - scaledWidth) / 2;
    const offsetY = padding + (drawHeight - scaledHeight) / 2;

    const mx = (wx - this.worldBounds.minX) * scale + offsetX;
    const my = (wy - this.worldBounds.minY) * scale + offsetY;

    return { x: mx, y: my };
  }

  /**
   * Update the minimap with current dungeon state
   */
  render(
    rooms: Map<string, Room>,
    corridors: Map<string, Corridor>,
    layout: HexLayout,
    viewportBounds: { x: number; y: number; width: number; height: number }
  ): void {
    const ctx = this.ctx;
    const { width, height, padding } = this.config;

    // Clear
    ctx.fillStyle = "rgba(30, 30, 30, 0.95)";
    ctx.fillRect(0, 0, width, height);

    // Calculate world bounds from all rooms
    this.worldBounds = this.calculateWorldBounds(rooms, corridors, layout, viewportBounds);

    if (!this.worldBounds) {
      // No rooms - show placeholder
      ctx.fillStyle = "#666";
      ctx.font = "12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("No rooms yet", width / 2, height / 2);
      return;
    }

    const worldWidth = this.worldBounds.maxX - this.worldBounds.minX;
    const worldHeight = this.worldBounds.maxY - this.worldBounds.minY;

    const drawWidth = width - padding * 2;
    const drawHeight = height - padding * 2;
    const scale = Math.min(drawWidth / worldWidth, drawHeight / worldHeight);

    // Calculate offset to center
    const scaledWidth = worldWidth * scale;
    const scaledHeight = worldHeight * scale;
    const offsetX = padding + (drawWidth - scaledWidth) / 2;
    const offsetY = padding + (drawHeight - scaledHeight) / 2;

    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);
    ctx.translate(-this.worldBounds.minX, -this.worldBounds.minY);

    // Draw corridors first
    ctx.fillStyle = "#a89880";
    for (const corridor of corridors.values()) {
      for (const hexKey of corridor.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        // Convert to world coords by subtracting origin
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        const hexSize = layout.size * 0.8;
        ctx.beginPath();
        ctx.arc(worldX, worldY, hexSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw rooms
    ctx.fillStyle = "#e8dcc8";
    ctx.strokeStyle = "#5c4a3d";
    ctx.lineWidth = 2 / scale; // Keep consistent line width
    for (const room of rooms.values()) {
      for (const hexKey of room.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        // Convert to world coords by subtracting origin
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        const hexSize = layout.size * 0.85;
        ctx.beginPath();
        ctx.arc(worldX, worldY, hexSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    ctx.restore();

    // Draw diff overlay if present
    if (this.diffState && this.diffLayout) {
      this.renderDiffOverlay(ctx, this.diffState, this.diffLayout, scale, offsetX, offsetY);
    }

    // Draw viewport rectangle
    const vpTopLeft = this.worldToMinimap(viewportBounds.x, viewportBounds.y);
    const vpBottomRight = this.worldToMinimap(
      viewportBounds.x + viewportBounds.width,
      viewportBounds.y + viewportBounds.height
    );

    if (vpTopLeft && vpBottomRight) {
      ctx.strokeStyle = "rgba(100, 200, 255, 0.8)";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 2]);
      ctx.strokeRect(
        vpTopLeft.x,
        vpTopLeft.y,
        vpBottomRight.x - vpTopLeft.x,
        vpBottomRight.y - vpTopLeft.y
      );
      ctx.setLineDash([]);
    }

    // Draw border
    ctx.strokeStyle = "#555";
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);
  }

  /**
   * Calculate the world bounds encompassing all rooms and the viewport
   */
  private calculateWorldBounds(
    rooms: Map<string, Room>,
    corridors: Map<string, Corridor>,
    layout: HexLayout,
    viewportBounds: { x: number; y: number; width: number; height: number }
  ): { minX: number; maxX: number; minY: number; maxY: number } | null {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    let hasContent = false;

    // Include all room hexes (convert to world coords)
    for (const room of rooms.values()) {
      for (const hexKey of room.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        // Convert to world coords
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        minX = Math.min(minX, worldX - layout.size);
        maxX = Math.max(maxX, worldX + layout.size);
        minY = Math.min(minY, worldY - layout.size);
        maxY = Math.max(maxY, worldY + layout.size);
        hasContent = true;
      }
    }

    // Include corridor hexes (convert to world coords)
    for (const corridor of corridors.values()) {
      for (const hexKey of corridor.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        // Convert to world coords
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        minX = Math.min(minX, worldX - layout.size);
        maxX = Math.max(maxX, worldX + layout.size);
        minY = Math.min(minY, worldY - layout.size);
        maxY = Math.max(maxY, worldY + layout.size);
        hasContent = true;
      }
    }

    if (!hasContent) return null;

    // Always include the viewport bounds with some margin
    const margin = layout.size * 2;
    minX = Math.min(minX, viewportBounds.x - margin);
    maxX = Math.max(maxX, viewportBounds.x + viewportBounds.width + margin);
    minY = Math.min(minY, viewportBounds.y - margin);
    maxY = Math.max(maxY, viewportBounds.y + viewportBounds.height + margin);

    // Add padding around content
    const padX = (maxX - minX) * 0.1;
    const padY = (maxY - minY) * 0.1;

    return {
      minX: minX - padX,
      maxX: maxX + padX,
      minY: minY - padY,
      maxY: maxY + padY,
    };
  }

  /**
   * Show or hide the minimap
   */
  setVisible(visible: boolean): void {
    this.canvas.style.display = visible ? "block" : "none";
  }

  /**
   * Render diff overlay showing added/removed rooms and corridors.
   */
  private renderDiffOverlay(
    ctx: CanvasRenderingContext2D,
    diff: StateDiff,
    layout: HexLayout,
    scale: number,
    offsetX: number,
    offsetY: number
  ): void {
    if (!this.worldBounds) return;

    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);
    ctx.translate(-this.worldBounds.minX, -this.worldBounds.minY);

    // Draw removed corridors (red, semi-transparent)
    ctx.fillStyle = "rgba(220, 60, 60, 0.6)";
    for (const corridor of diff.removedCorridors) {
      for (const hexKey of corridor.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        const hexSize = layout.size * 0.8;
        ctx.beginPath();
        ctx.arc(worldX, worldY, hexSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw added corridors (green, semi-transparent)
    ctx.fillStyle = "rgba(60, 180, 90, 0.6)";
    for (const corridor of diff.addedCorridors) {
      for (const hexKey of corridor.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        const hexSize = layout.size * 0.8;
        ctx.beginPath();
        ctx.arc(worldX, worldY, hexSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw removed rooms (red with border)
    ctx.fillStyle = "rgba(220, 60, 60, 0.6)";
    ctx.strokeStyle = "rgba(180, 40, 40, 0.9)";
    ctx.lineWidth = 3 / scale;
    for (const room of diff.removedRooms) {
      for (const hexKey of room.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        const hexSize = layout.size * 0.85;
        ctx.beginPath();
        ctx.arc(worldX, worldY, hexSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Draw added rooms (green with border)
    ctx.fillStyle = "rgba(60, 180, 90, 0.6)";
    ctx.strokeStyle = "rgba(40, 140, 60, 0.9)";
    ctx.lineWidth = 3 / scale;
    for (const room of diff.addedRooms) {
      for (const hexKey of room.hexes) {
        const hex = keyToHex(hexKey);
        const screenPos = hexToPixel(hex, layout);
        const worldX = screenPos.x - layout.origin.x;
        const worldY = screenPos.y - layout.origin.y;
        const hexSize = layout.size * 0.85;
        ctx.beginPath();
        ctx.arc(worldX, worldY, hexSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.canvas.removeEventListener("click", this.handleClick);
    this.canvas.removeEventListener("mousedown", this.handleMouseDown);
    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}
